<script lang="ts">
	import { onMount } from 'svelte';

	let showFloatingCTA = $state(false);
	let calendlyLoaded = $state(false);

	onMount(() => {
		const onScroll = () => {
			showFloatingCTA = window.scrollY > 300;
		};
		window.addEventListener('scroll', onScroll, { passive: true });

		const script = document.createElement('script');
		script.src = 'https://assets.calendly.com/assets/external/widget.js';
		script.async = true;
		script.onload = () => (calendlyLoaded = true);
		document.head.appendChild(script);

		const link = document.createElement('link');
		link.rel = 'stylesheet';
		link.href = 'https://assets.calendly.com/assets/external/widget.css';
		document.head.appendChild(link);

		return () => {
			window.removeEventListener('scroll', onScroll);
		};
	});

	function openCalendly() {
		const CALENDLY_URL = 'https://calendly.com/juslin03/30min';
		if ((window as any).Calendly) {
			(window as any).Calendly.initPopupWidget({ url: CALENDLY_URL });
		} else {
			window.open(CALENDLY_URL, '_blank');
		}
	}
</script>

<div
	id="audit"
	class="floating-cta-wrapper fixed right-5 bottom-24 z-50 sm:right-6"
	class:floating-visible={showFloatingCTA}
>
	<!-- Pulse ring -->
	<span
		class="animate-ping-slow pointer-events-none absolute inset-0 rounded-full bg-juslin-gold/30"
	></span>

	<button
		onclick={openCalendly}
		class="floating-cta group relative flex cursor-pointer items-center gap-2.5 overflow-hidden rounded-full bg-juslin-gold px-4 py-3 text-sm font-bold text-juslin-black shadow-xl shadow-juslin-gold/30 transition-all duration-300 hover:gap-3 hover:px-5 hover:shadow-2xl hover:shadow-juslin-gold/40 sm:px-5 sm:py-3.5 sm:text-base"
		aria-label="Réserver un appel"
	>
		<!-- Calendar icon -->
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="currentColor"
			class="h-4 w-4 shrink-0 sm:h-5 sm:w-5"
		>
			<path
				d="M12.75 12.75a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM7.5 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM8.25 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM9.75 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM10.5 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM12 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM12.75 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM14.25 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM15 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM16.5 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM15 12.75a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM16.5 13.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
			/>
			<path
				fill-rule="evenodd"
				d="M6.75 2.25A.75.75 0 0 1 7.5 3v1.5h9V3A.75.75 0 0 1 18 3v1.5h.75a3 3 0 0 1 3 3v11.25a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3V7.5a3 3 0 0 1 3-3H6V3a.75.75 0 0 1 .75-.75Zm13.5 9a1.5 1.5 0 0 0-1.5-1.5H5.25a1.5 1.5 0 0 0-1.5 1.5v7.5a1.5 1.5 0 0 0 1.5 1.5h13.5a1.5 1.5 0 0 0 1.5-1.5v-7.5Z"
				clip-rule="evenodd"
			/>
		</svg>

		<!-- Label with slide-in effect -->
		<span
			class="max-w-0 overflow-hidden whitespace-nowrap transition-all duration-300 group-hover:max-w-xs"
		>
			Réserver un appel
		</span>

		<!-- Arrow -->
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 20 20"
			fill="currentColor"
			class="h-3.5 w-3.5 shrink-0 translate-x-0 transition-transform duration-300 group-hover:translate-x-0.5"
		>
			<path
				fill-rule="evenodd"
				d="M3 10a.75.75 0 0 1 .75-.75h10.638L10.23 5.29a.75.75 0 1 1 1.04-1.08l5.5 5.25a.75.75 0 0 1 0 1.08l-5.5 5.25a.75.75 0 1 1-1.04-1.08l4.158-3.96H3.75A.75.75 0 0 1 3 10Z"
				clip-rule="evenodd"
			/>
		</svg>
	</button>
</div>

<style>
	/* ── Floating CTA ── */
	.floating-cta-wrapper {
		opacity: 0;
		transform: translateY(1rem) scale(0.9);
		pointer-events: none;
		transition:
			opacity 0.35s ease,
			transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
	}
	.floating-cta-wrapper.floating-visible {
		opacity: 1;
		transform: translateY(0) scale(1);
		pointer-events: auto;
	}

	/* Slow pulse ring */
	@keyframes ping-slow {
		75%,
		100% {
			transform: scale(2);
			opacity: 0;
		}
	}
	:global(.animate-ping-slow) {
		animation: ping-slow 2.5s cubic-bezier(0, 0, 0.2, 1) infinite;
	}

	/* Label expand on hover (extra safety for older browsers) */
	.floating-cta span {
		transition:
			max-width 0.35s cubic-bezier(0.4, 0, 0.2, 1),
			opacity 0.25s ease;
		opacity: 0;
	}
	.floating-cta:hover span {
		opacity: 1;
	}

	/* ── Slide-up animation ── */
	@keyframes slide-up {
		from {
			opacity: 0;
			transform: translateY(1.5rem);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}
	:global(.animate-slide-up) {
		animation: slide-up 0.5s ease both;
	}

	/* ── Fade-in ── */
	@keyframes fade-in {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}
	:global(.animate-fade-in) {
		animation: fade-in 0.6s ease both;
	}
</style>
